import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationMessageStats } from '@/lib/gupshup-client';
import type { Message } from '@/components/messages/types';
import { useNow } from '@/hooks/use-now';

/** Three or more operator bubbles inside this window read as one thought split into many sends. */
const BURST_WINDOW_MS = 2 * 60 * 1000;
const BURST_SIZE = 3;
/** A short draft right after an operator bubble is likely a follow-up that belonged in it. */
const SHORT_DRAFT_CHARS = 20;
const FOLLOW_UP_WINDOW_MS = 60 * 1000;
const CLOCK_TICK_MS = 5000;

/** An outgoing session message typed by a person (not the agent, not a template). */
function isOperatorBubble(message: Message): boolean {
  return (
    message.direction === 'outbound' &&
    message.metadata?.role === 'human' &&
    !message.template &&
    message.messageType !== 'template'
  );
}

function lastOperatorBubble(messages: Message[]): Message | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isOperatorBubble(messages[i])) return messages[i];
  }
  return undefined;
}

function sentAt(message: Message): number {
  return new Date(message.createdAt).getTime();
}

/**
 * D17: nudge the operator to batch, without ever blocking the send. Shown when the last three
 * messages are operator bubbles sent within two minutes (and the latest of them still is), or
 * when the draft is short and the last operator bubble went out under a minute ago.
 */
export function shouldShowBatchHint(messages: Message[], draft: string, now: number): boolean {
  const lastThree = messages.slice(-BURST_SIZE);
  if (lastThree.length === BURST_SIZE && lastThree.every(isOperatorBubble)) {
    const first = sentAt(lastThree[0]);
    const last = sentAt(lastThree[BURST_SIZE - 1]);
    if (last - first <= BURST_WINDOW_MS && now - last <= BURST_WINDOW_MS) return true;
  }

  const trimmed = draft.trim();
  if (trimmed.length === 0 || trimmed.length >= SHORT_DRAFT_CHARS) return false;
  const lastOperator = lastOperatorBubble(messages);
  return !!lastOperator && now - sentAt(lastOperator) < FOLLOW_UP_WINDOW_MS;
}

/**
 * The batching hint under the composer. It owns a coarse clock so the hint also disappears once
 * the windows pass, without re-rendering the whole message view on every tick. The clock only runs
 * while the chat has an operator bubble at all; both windows are measured from one.
 */
export function ComposerBatchHint({ messages, draft }: { messages: Message[]; draft: string }) {
  const { t } = useTranslation('conversations');
  const lastOperatorAt = useMemo(() => {
    const lastOperator = lastOperatorBubble(messages);
    return lastOperator ? sentAt(lastOperator) : null;
  }, [messages]);
  const now = useNow(lastOperatorAt === null ? null : CLOCK_TICK_MS);

  if (!shouldShowBatchHint(messages, draft, now)) return null;

  return (
    <p role="status" className="text-xs text-chat-notice-text">
      {t('messageView.batchHint')}
    </p>
  );
}

// 'always' groups four-digit numbers too ("1.000" in es, where the default leaves "1000"). The ES2020
// lib types `useGrouping` as boolean; engines that predate the string values coerce it to true.
const ALWAYS_GROUP = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions;

/**
 * D17: the generic cost line, then this chat's and the line's counts when the stats loaded. No price
 * per message, ever. Without stats (loading or an error) only the generic sentence shows.
 */
export function ComposerCostLine({ stats }: { stats?: ConversationMessageStats }) {
  const { t, i18n } = useTranslation('conversations');
  const language = i18n.resolvedLanguage ?? i18n.language;
  const format = useMemo(() => new Intl.NumberFormat(language, ALWAYS_GROUP).format, [language]);

  const parts = [t('messageView.costLine')];
  if (stats) {
    parts.push(
      t('messageView.costChatCount', { n: format(stats.conversation.sentThisMonth) }),
      t('messageView.costFreeTier', {
        used: format(stats.line.freeTierUsed),
        limit: format(stats.line.freeTierLimit),
      }),
    );
  }

  return <p className="text-xs text-chat-text-muted">{parts.join(' · ')}</p>;
}
